// SOS Service API utility — talks to prvsk_sos_service via nginx.
//
// Production (nginx routes /api/responder/ → 127.0.0.1:9002/):
//   Default: same origin + /api/responder  (no env var needed)
//
// Development (direct to port 9002):
//   Set REACT_APP_SOS_API_BASE=http://localhost:9002

const _sosBase = process.env.REACT_APP_SOS_API_BASE
  || `${window.location.origin}/api/responder`;

export const SOS_API = _sosBase;

// ── Token management ──────────────────────────────────────────────────────────

const _SOS_TOKEN_KEY = 'sos_token';
let _sosToken = sessionStorage.getItem(_SOS_TOKEN_KEY) || null;

export function setSosToken(t) {
  _sosToken = t || null;
  if (_sosToken) sessionStorage.setItem(_SOS_TOKEN_KEY, _sosToken);
  else           sessionStorage.removeItem(_SOS_TOKEN_KEY);
}

export function resetSosSession() {
  _sosToken = null;
  sessionStorage.removeItem(_SOS_TOKEN_KEY);
}

export function getSosToken() {
  return _sosToken;
}

// ── Authenticated fetch ────────────────────────────────────────────────────────

export async function sosFetch(path, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  if (_sosToken) headers['Authorization'] = `Bearer ${_sosToken}`;
  if (opts.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    const { json, ...rest } = opts;
    return fetch(`${SOS_API}${path}`, { ...rest, headers, body: JSON.stringify(json) });
  }
  return fetch(`${SOS_API}${path}`, { ...opts, headers });
}

// ── Push registration for responders ─────────────────────────────────────────
// Registers the same Firebase FCM token with the SOS service instead of the
// Parvekshak device API, so the responder receives SOS push notifications.

export async function initResponderPush() {
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return;
  if (Notification.permission === 'denied') return;
  if (!_sosToken) return;

  const FIREBASE_CONFIG = {
    apiKey:            process.env.REACT_APP_FIREBASE_API_KEY            || 'AIzaSyAtJqyVPWyuR6Klb1r-0G7-djQs9ZhYLxU',
    authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN        || 'parvekshakapp.firebaseapp.com',
    projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID         || 'parvekshakapp',
    storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET     || 'parvekshakapp.firebasestorage.app',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '962813517310',
    appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  };
  const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (!FIREBASE_CONFIG.appId || !VAPID_KEY) return;

  try {
    const fb = window.firebase;
    if (!fb) return;
    if (!fb.apps.length) fb.initializeApp(FIREBASE_CONFIG);
    const messaging = fb.messaging();

    const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
    const token = await messaging.getToken({ vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });

    if (token) {
      await sosFetch('/sos/push/register', {
        method: 'POST',
        json: {
          token,
          platform:     'web',
          device_model: navigator.userAgent.slice(0, 120),
        },
      });
    }

    // Foreground SOS messages
    messaging.onMessage((payload) => {
      const isSOS = payload.data?.sos === 'true';
      if (isSOS) {
        const audio = new Audio('/sounds/siren_double.wav');
        audio.play().catch(() => {});
      }
      const title = payload.notification?.title || payload.data?.title || '🆘 SOS Alert';
      const body  = payload.notification?.body  || payload.data?.body  || '';
      if (Notification.permission === 'granted') {
        new Notification(title, { body, icon: '/icon-192.png' });
      }
    });
  } catch (err) {
    console.warn('[SOS-Push] Registration failed:', err.message);
  }
}
